import { useStyletron } from '@thanx/uikit/theme'
import { useHashTab } from 'components/Tabs/useHashTab'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { useReportPermission } from 'hooks/useReportPermission'
import { useCallback } from 'react'
import { history } from 'store'

// The four production Looker dashboards built for DATA-4416 (thanx-looker#377):
// Campaign Performance (530), Code Utilization (531), Geographic Breakdown
// (532), Channel Breakdown (533). IDs are hardcoded because the dashboards are
// stable, published assets — swapping one is a code deploy like any other route
// change, and keeping the IDs in code means the report renders correctly with
// no LaunchDarkly configuration to coordinate.
export type PromoCodeDashboardIds = {
  campaign_performance: string
  code_utilization: string
  geographic_breakdown: string
  channel_breakdown: string
}

export const DASHBOARD_IDS: PromoCodeDashboardIds = {
  campaign_performance: 'dashboards/530',
  code_utilization: 'dashboards/531',
  geographic_breakdown: 'dashboards/532',
  channel_breakdown: 'dashboards/533',
}

export enum Tabs {
  CAMPAIGN_PERFORMANCE = 'campaign_performance',
  CODE_UTILIZATION = 'code_utilization',
  GEOGRAPHIC_BREAKDOWN = 'geographic_breakdown',
  CHANNEL_BREAKDOWN = 'channel_breakdown',
}

const isTab = (value: string): value is Tabs =>
  (Object.values(Tabs) as string[]).includes(value)

const ALL_TABS = Object.values(Tabs)

export const usePromoCodeReport = () => {
  const promoCodesEnabled = useFlag('mx-universal-promo-codes', false)
  const dashboardIds = DASHBOARD_IDS
  const merchant = useCurrentMerchant()
  const [css] = useStyletron()
  // Seeds from the hash and keeps following it: a shared #code_utilization
  // link opened while this page is already up changes only the hash.
  const [activeTab, setActiveTab] = useHashTab(
    ALL_TABS,
    Tabs.CAMPAIGN_PERFORMANCE
  )
  const reportPermission = useReportPermission({
    rbac: 'Reports',
    legacy: 'LocationReports',
  })

  const onTabChange = useCallback((tab: string | null) => {
    if (tab && isTab(tab)) {
      setActiveTab(tab)
      // Spread the current location so a query string survives the tab
      // switch — `history.replace({ hash })` alone resets `search` to ''.
      history.replace({ ...history.location, hash: tab })
    }
  }, [])

  return {
    promoCodesEnabled,
    dashboardIds,
    merchant,
    css,
    activeTab,
    onTabChange,
    reportPermission,
  }
}
